import { useMemo, useState, useEffect } from "react";
import {
  CheckCircle,
  AlertCircle,
  X,
  RefreshCw,
  Inbox,
  Sparkles,
  PhoneCall,
  Wallet,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useOrders } from "../../hooks/useOrders";
import { FiltersBar } from "../../components/orders/FiltersBar";
import { OrdersTable } from "../../components/orders/OrdersTable";
import { OrderModal } from "../../components/orders/OrderModal";
import Loading from "../../../../ui/Loading";
import {
  ordersUpdateStatus,
} from "../../../../../api/auth";
import { city } from "../../../../../api/City";
import { PENDING_TABS, localStatusMeta } from "../../components/ui/orderStatuses";

function MetricCard({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className="metric-card flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</div>
        <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
        {hint ? <div className="text-xs text-slate-500">{hint}</div> : null}
      </div>
    </div>
  );
}

const ALLOWED_STATUSES = [
  "new",
  "pending_confirmation",
  "call_scheduled",
  "no_answer",
  "busy",
  "wrong_number",
  "call_back_later",
  "rejected",
  "invalid_address",
  "need_info",
  "postponed",
  "rescheduled",
];

export default function PendingOrders() {
  const {
    orders,
    loading,
    saving,
    error,
    filters,
    setFilters,
    page,
    setPage,
    refresh,
    drafts,
    updateDraft,
    saveOrder,
  } = useOrders({
    filters: {},
    page_size: 20,
  });

  const [pageSize, setPageSize] = useState(10);
  const [tab, setTab] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewOrder, setViewOrder] = useState(null);
  const [cities, setCities] = useState([]);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [pageSize, filters, tab, setPage]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await city();
        setCities(data || []);
      } catch (err) {
        setCities([]);
        setMessage({
          type: "error",
          text: "Failed to load cities.",
        });
      }
    };

    loadCities();
  }, []);

  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      setMessage(null);
    }, 4000);

    return () => clearTimeout(timer);
  }, [message]);

  const handleQuickStatus = async (orderId, status) => {
    try {
      await ordersUpdateStatus(orderId, {
        local_status: status,
      });

      updateDraft(orderId, { local_status: status });

      setMessage({
        type: "success",
        text: `Statut mis à jour : ${localStatusMeta(status).label}.`,
      });

      // la commande quitte la liste "en attente" (ex. confirmée) -> recharger
      if (!ALLOWED_STATUSES.includes(status)) {
        await refresh();
      }
    } catch (error) {
      const data = error?.response?.data || {};
      setMessage({
        type: "error",
        text:
          data.detail ||
          data.message ||
          (Array.isArray(data.local_status) ? data.local_status[0] : data.local_status) ||
          "Impossible de mettre à jour le statut.",
      });
    }
  };

  // Toutes les commandes "en attente" (base des compteurs et des onglets)
  const pendingOrders = useMemo(() => {
    const normalizedAllowedStatuses = ALLOWED_STATUSES.map((s) =>
      String(s).toLowerCase()
    );
    return orders.filter((o) => {
      const localStatusOk = normalizedAllowedStatuses.includes(
        String(o.local_status || "").toLowerCase()
      );
      const shippingPending =
        !o.shipping_status ||
        String(o.shipping_status).toLowerCase() === "pending";
      return localStatusOk && shippingPending;
    });
  }, [orders]);

  const tabCounts = useMemo(() => {
    const counts = {};
    PENDING_TABS.forEach((t) => {
      counts[t.key] = t.statuses
        ? pendingOrders.filter((o) => t.statuses.includes(o.local_status)).length
        : pendingOrders.length;
    });
    return counts;
  }, [pendingOrders]);

  const pendingAmount = useMemo(
    () => pendingOrders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0),
    [pendingOrders]
  );

  const visibleOrders = useMemo(() => {
    const getCityValue = (order) => {
      if (order?.shipping_city_id != null) return String(order.shipping_city_id);
      if (order?.shipping_city?.id != null) return String(order.shipping_city.id);
      if (order?.shipping_city != null) return String(order.shipping_city);
      return "";
    };

    const activeTab = PENDING_TABS.find((t) => t.key === tab);
    let result = activeTab?.statuses
      ? pendingOrders.filter((o) => activeTab.statuses.includes(o.local_status))
      : pendingOrders;

    if (filters.search) {
      const q = String(filters.search).toLowerCase();

      result = result.filter(
        (o) =>
          String(o.client_name || "").toLowerCase().includes(q) ||
          String(o.client_phone || "").toLowerCase().includes(q) ||
          String(o.produit || "").toLowerCase().includes(q)
      );
    }

    if (filters.city) {
      result = result.filter((o) => getCityValue(o) === String(filters.city));
    }

    if (filters.local_status) {
      result = result.filter(
        (o) =>
          String(o.local_status || "").toLowerCase() ===
          String(filters.local_status).toLowerCase()
      );
    }

    if (filters.shipping_status) {
      result = result.filter(
        (o) =>
          String(o.shipping_status || "").toLowerCase() ===
          String(filters.shipping_status).toLowerCase()
      );
    }

    if (filters.date_from) {
      const start = new Date(filters.date_from);
      start.setHours(0, 0, 0, 0);

      result = result.filter((o) => new Date(o.created_at) >= start);
    }

    if (filters.date_to) {
      const end = new Date(filters.date_to);
      end.setHours(23, 59, 59, 999);

      result = result.filter((o) => new Date(o.created_at) <= end);
    }

    return result.sort((a, b) => {
      const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b?.created_at ? new Date(b.created_at).getTime() : 0;

      if (da !== 0 || db !== 0) return db - da;

      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [pendingOrders, filters, tab]);

  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return visibleOrders.slice(start, end);
  }, [visibleOrders, page, pageSize]);

  const computedTotalPages = Math.max(
    1,
    Math.ceil(visibleOrders.length / pageSize)
  );

  const handleSave = async (orderId) => {
    await saveOrder(orderId);
  };

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
    );
  };

  const onChangeFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      city: "",
      local_status: "",
      shipping_status: "",
      date_from: "",
      date_to: "",
    });
  };

  return (
    <div className="page-shell space-y-5">
      {message && (
        <div
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
            message.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-rose-200 bg-rose-50 text-rose-700"
          }`}
          role="status"
        >
          <div className="pt-0.5">
            {message.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          </div>
          <div className="flex-1 text-sm font-medium">{message.text}</div>
          <button type="button" onClick={() => setMessage(null)} className="opacity-70 transition hover:opacity-100">
            <X size={16} />
          </button>
        </div>
      )}

      {/* En-tête */}
      <div className="page-header-card flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Confirmation</div>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">Commandes en attente</h1>
          <p className="mt-1 text-sm text-slate-500">
            Appelez, confirmez ou reprogrammez vos commandes. « Détails » affiche l'historique du client dans votre boutique.
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={loading} className="btn-outline self-start md:self-auto">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* Indicateurs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard icon={Inbox} label="En attente" value={tabCounts.all ?? 0} tone="bg-indigo-50 text-indigo-600" />
        <MetricCard icon={Sparkles} label="Nouvelles" value={tabCounts.new ?? 0} tone="bg-sky-50 text-sky-600" />
        <MetricCard
          icon={PhoneCall}
          label="À rappeler"
          value={(tabCounts.callback ?? 0) + (tabCounts.no_answer ?? 0)}
          hint={`${tabCounts.no_answer ?? 0} sans réponse`}
          tone="bg-amber-50 text-amber-600"
        />
        <MetricCard
          icon={Wallet}
          label="Montant en attente"
          value={`${pendingAmount.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} MAD`}
          tone="bg-emerald-50 text-emerald-600"
        />
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Impossible de charger les commandes en attente.
        </div>
      )}

      {/* Onglets par statut */}
      <div className="flex gap-1 overflow-x-auto rounded-full border border-slate-200 bg-white p-1 scrollbar-none dark:border-slate-800 dark:bg-slate-900">
        {PENDING_TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                active ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
              }`}
            >
              {t.label}
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                {tabCounts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <FiltersBar
        filters={filters}
        onChange={onChangeFilters}
        onReset={resetFilters}
        city={cities}
        statuses={ALLOWED_STATUSES}
      />

      {/* Tableau */}
      <div className="surface-card overflow-hidden rounded-xl border border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div className="text-sm text-slate-500">
            {loading ? "Chargement…" : (
              <>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{visibleOrders.length}</span> commande(s)
                {selectedIds.length ? ` · ${selectedIds.length} sélectionnée(s)` : ""}
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-500">
              Afficher
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <div className="flex items-center gap-1">
              <button
                type="button"
                className="icon-button !h-8 !w-8 rounded-lg border border-slate-200 disabled:opacity-40 dark:border-slate-700"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-sm text-slate-500">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{page}</span> / {computedTotalPages}
              </span>
              <button
                type="button"
                className="icon-button !h-8 !w-8 rounded-lg border border-slate-200 disabled:opacity-40 dark:border-slate-700"
                disabled={page >= computedTotalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-12">
            <Loading text="Chargement des commandes…" variant="dots" />
          </div>
        ) : (
          <OrdersTable
            orders={paginatedOrders}
            drafts={drafts}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onView={(o) => setViewOrder(o)}
            onQuickStatus={handleQuickStatus}
            onChangeDraft={updateDraft}
            onSaveRow={handleSave}
            saving={saving}
            mode="new"
            onRefresh={refresh}
            city={cities}
          />
        )}
      </div>

      {viewOrder && (
        <OrderModal
          order={viewOrder}
          onClose={() => setViewOrder(null)}
          onSaved={refresh}
          city={cities}
        />
      )}
    </div>
  );
}
